import { Gauge, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/cn";
import type { AccountSettings } from "@/lib/accounts";
import { describePacing, estimateCampaignDuration, formatDuration, formatInterval } from "@/lib/pacing";
import { PACING_PRESETS, matchPreset } from "@/lib/pacing-presets";

/** Trên mức này thì nhịp gửi nhanh hơn hẳn một người dùng Zalo bình thường. */
const AGGRESSIVE_PER_HOUR = 150;
/** Trên mức này thì không còn giống hành vi người dùng ở bất kỳ mức nào. */
const EXTREME_PER_HOUR = 600;

/**
 * Dịch các con số cấu hình thành nhịp gửi thực tế.
 *
 * Đặt "8000 ms" cạnh "30 tin/giờ" thì không ai đoán được thực tế chạy ra sao —
 * hai giá trị đó mâu thuẫn nhau và cái chặt hơn sẽ thắng. Hiện thẳng kết quả
 * để chỉnh có căn cứ.
 */
export function PacingSummary({
    settings,
    groupCount,
    onApplyPreset,
}: {
    settings: AccountSettings;
    groupCount: number;
    onApplyPreset: (next: Partial<AccountSettings>) => void;
}) {
    const pacing = describePacing(settings);
    const active = matchPreset(settings);
    const sample = Math.max(1, Math.min(groupCount, pacing.perDay));
    const duration = estimateCampaignDuration(settings, sample);
    const allGroups = groupCount > 0 ? estimateCampaignDuration(settings, groupCount) : 0;

    return (
        <div className="space-y-2.5 rounded-lg bg-surface-muted px-3 py-2.5">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm">
                <span className="flex items-center gap-1.5 font-medium text-ink">
                    <Gauge className="size-4 text-ink-muted" aria-hidden />
                    Thực tế
                </span>

                <span className="text-ink-muted">
                    mỗi <span className="text-ink">{formatInterval(pacing.intervalMs)}</span> một tin
                </span>
                <span className="text-ink-muted">
                    ≈ <span className="text-ink">{pacing.perHour}</span> tin/giờ ·{" "}
                    <span className="text-ink">{pacing.perDay}</span> tin/ngày
                </span>

                {groupCount > 0 && (
                    <span className="text-ink-muted">
                        {groupCount} nhóm mất <span className="text-ink">{formatDuration(allGroups)}</span>
                        {sample < groupCount && ` (${sample} nhóm đầu: ${formatDuration(duration)})`}
                    </span>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-ink-muted">Tốc độ:</span>
                {PACING_PRESETS.map((preset) => (
                    <button
                        key={preset.id}
                        type="button"
                        onClick={() => onApplyPreset(preset.settings)}
                        title={preset.hint}
                        className={cn(
                            "rounded-lg border px-2.5 py-1 text-xs transition",
                            active?.id === preset.id
                                ? "border-brand bg-brand-soft font-medium text-brand"
                                : "border-line bg-surface text-ink-muted hover:text-ink",
                        )}
                    >
                        {preset.label} · {preset.hint}
                    </button>
                ))}
                {!active && <span className="text-xs text-ink-muted">tuỳ chỉnh</span>}
            </div>

            <p className="text-xs text-ink-muted">
                {pacing.cappedByHour
                    ? `Nhịp do hai ô nghỉ quyết định. Chạm trần ${settings.maxPerHour} tin/giờ sau ~${pacing.burstMinutes} phút thì dừng chờ sang giờ mới`
                    : "Nhịp do hai ô nghỉ quyết định — muốn nhanh hơn thì hạ hai ô đó xuống"}
            </p>

            {pacing.perHour >= EXTREME_PER_HOUR ? (
                <p className="flex items-start gap-1.5 text-xs text-danger">
                    <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                    <span>
                        <span className="font-medium">Rủi ro cao.</span> Nhịp {pacing.perHour} tin/giờ không
                        giống hành vi người dùng ở bất kỳ mức nào — đây là mức dễ bị Zalo hạn chế nhất. Chỉ nên
                        dùng khi chấp nhận được rủi ro mất tài khoản. Hệ thống vẫn tự dừng sau 5 lỗi liên tiếp.
                    </span>
                </p>
            ) : pacing.perHour >= AGGRESSIVE_PER_HOUR ? (
                <p className="flex items-start gap-1.5 text-xs text-warning">
                    <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                    Nhịp này nhanh hơn nhiều so với người dùng thật. Chạy thử một nhóm nhỏ vài ngày trước khi
                    mở hết — nếu Zalo hạn chế, hệ thống sẽ tự dừng sau 5 lỗi liên tiếp.
                </p>
            ) : null}
        </div>
    );
}
